import { useState } from "react";
import { X, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * QuickCustomerDialog — V1.1.1 OP-001 (Batch B #6).
 *
 * Creates a temporary customer with only account# + name so dispatch
 * isn't blocked waiting for the ERP nightly. The record is marked
 * `source: "quick"` and the customer_import will auto-enrich it on the
 * next run (no duplicate).
 *
 * Admin/Office Staff only. Driver role sees no button.
 */
export default function QuickCustomerDialog({ open, onClose, onCreated }) {
  const [accountNumber, setAccountNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setAccountNumber("");
    setCustomerName("");
    setSaving(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const acct = accountNumber.trim();
    const name = customerName.trim();
    if (!acct || !name) {
      toast.error("Account number and customer name are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/customers/quick", {
        account_number: acct,
        customer_name: name,
      });
      toast.success(`Quick customer #${acct} created — will enrich on next ERP import.`);
      onCreated?.(r.data);
      reset();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create quick customer");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="quick-customer-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 font-display text-base font-semibold text-slate-900">
              <UserPlus size={16} weight="bold" /> Quick Customer
            </div>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Create a temporary operational customer. The ERP remains the
              authoritative master — the next import will enrich or merge
              this record automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
            data-testid="quick-customer-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Account Number *
            </label>
            <input
              data-testid="quick-customer-account"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              autoFocus
              placeholder="e.g. 3049920"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 font-mono text-sm text-slate-900 focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Customer Name *
            </label>
            <input
              data-testid="quick-customer-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="e.g. Charlie's Auto Repair"
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-brand focus:outline-none"
            />
          </div>
          <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-800">
            The customer will show a <span className="font-semibold">Quick</span> badge
            until the next ERP import. Address, phone, and other fields will be filled
            in automatically at that time.
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="quick-customer-cancel"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="quick-customer-submit"
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create Quick Customer"}
          </button>
        </div>
      </form>
    </div>
  );
}
